import React, { useState, useEffect } from 'react';
import { Table, Tooltip, Popconfirm, Checkbox, message, Button, Card, Row, Col, Typography } from 'antd';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import ApiService from '../Components/apiService';
import { BASE_URL } from '../Components/Constant';
import { useSelector } from 'react-redux';
import CreateAppModal from './CreateAppModal';
import AppDetail from './AppDetail'; // import the detail component

const { Title } = Typography;

const AppManager = () => {
  const [apps, setApps] = useState([]);
  const [pageSize, setPageSize] = useState(10);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedApp, setSelectedApp] = useState(null);
  const security = useSelector((state) => state.data.items[0]);

  // New state to toggle detail view
  const [showDetail, setShowDetail] = useState(false);

  useEffect(() => {
    fetchApps();
  }, []);

  const fetchApps = async () => {
    try {
      const apiService = new ApiService(`${BASE_URL}/mt_apps`, { 'Content-Type': 'application/json' }, 'GET');
      const res = await apiService.makeCall();
      setApps(Array.isArray(res) ? res : []);
    } catch (error) {
      console.error('Error fetching apps:', error);
    }
  };

  const handleDeleteApp = async (appId) => {
    if (security?.system_settings?.apps?.delete === false) {
      message.error('You do not have permission to delete');
      return;
    }
    try {
      const apiService = new ApiService(`${BASE_URL}/mt_apps/${appId}`, { 'Content-Type': 'application/json' }, 'DELETE');
      await apiService.makeCall();
      message.success('App deleted successfully!');
      fetchApps();
    } catch (error) {
      console.error('Error deleting app:', error);
    }
  };

  const handleEditApp = (record) => {
    if (security?.system_settings?.apps?.edit === false) {
      message.error('You do not have permission to edit');
      return;
    }
    setSelectedApp(record);
    setModalOpen(true);
  };

  const handleCreateApp = () => {
    if (security?.system_settings?.apps?.create === false) {
      message.error('You do not have permission to create');
      return;
    }
    setSelectedApp(null);
    setModalOpen(true);
  };

  const handleModalSave = async (values) => {
    try {
      const body = { mt_app: values };
        console.log('Saving app with body:', body);
        console.log('Selected app:', selectedApp);
      if (selectedApp?._id) {
        const apiService = new ApiService(
          `${BASE_URL}/mt_apps/${selectedApp._id}`,
          { 'Content-Type': 'application/json' },
          'PUT',
          body
        );
        await apiService.makeCall();
        message.success('App updated successfully');
      } else {
        const apiService = new ApiService(
          `${BASE_URL}/mt_apps`,
          { 'Content-Type': 'application/json' },
          'POST',
          body
        );
        await apiService.makeCall();
        message.success('App created successfully');
      }

      setModalOpen(false);
      fetchApps();
    } catch (error) {
      console.error('Error saving app:', error);
      message.error('Failed to save app');
    }
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (text, record) => (
        <span
          style={{ cursor: "pointer", color: "#1890ff" }}
          onClick={() => {
            if(security?.system_settings?.apps?.edit === false){
                message.error('You dont have permission to edit ');
                return;
              }
            setSelectedApp(record); // pass id to detail component
            setShowDetail(true);           // toggle detail view
          }}
        >
          {text}
        </span>
      ),
    },
    { title: 'Label', dataIndex: 'label' },
    { title: 'Type', dataIndex: 'type' },
    { title: 'Active', dataIndex: 'active', render: (val) => <Checkbox checked={val} disabled /> },
    { title: 'Default', dataIndex: 'default', render: (val) => <Checkbox checked={val} disabled /> },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <>
          <Tooltip title="Edit">
            <EditOutlined
              onClick={() => handleEditApp(record)}
              style={{ marginRight: 5, fontSize: '14px', cursor: 'pointer' }}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Popconfirm
              title="Are you sure you want to delete this app?"
              onConfirm={() => handleDeleteApp(record.id)}
              okText="Yes"
              cancelText="No"
            >
              <DeleteOutlined style={{ color: 'red', fontSize: '14px', cursor: 'pointer' }} />
            </Popconfirm>
          </Tooltip>
        </>
      ),
    },
  ];

  // If showDetail is true, render AppDetail instead of table
  if (showDetail) {
    return (
      <AppDetail
        record={selectedApp}
        onBack={() => setShowDetail(false)} // callback to go back to table
      />
    );
  }

  return (
    <div>
      <Card>
        <Row justify="space-between" align="middle" style={{ marginBottom: 20 }}>
          <Col><Title level={4}>Apps</Title></Col>
          <Col><Button type="primary" onClick={handleCreateApp}>Create App</Button></Col>
        </Row>
        <Table
          columns={columns}
          dataSource={apps}
          rowKey="id"
          scroll={{ x: 'max-content', y: 550 }}
          pagination={{
            pageSize: pageSize,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            onChange: (page, pageSize) => setPageSize(pageSize),
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
          }}
        />
      </Card>

      {/* Create/Edit Modal */}
      {modalOpen && (
        <CreateAppModal
          open={modalOpen}
          onCancel={() => setModalOpen(false)}
          onSave={handleModalSave}
          editingApp={selectedApp}
        />
      )}
    </div>
  );
};

export default AppManager;
